import type { Phrase } from '../types';

/** Strings for the pages area of the public site. Every key starts with `pg`. */
export const pagesEn = {
  // shared
  pgVenueCount: { one: '{count} suitable venue', other: '{count} suitable venues' },
  pgVenueCountNone: 'Venues joining soon',
  pgVenues: { one: '{count} venue', other: '{count} venues' },
  pgBrowseAll: 'Browse all venues',
  pgFindVenue: 'Find a venue',
  pgLoadError: 'We could not load venues just now. Please check your connection and try again.',
  pgRetry: 'Try again',
  pgBreadcrumb: 'Breadcrumb',
  pgReadMins: '{count} min read',
  pgReadGuide: 'Read guide',
  pgEnglishOnly: 'This guide is written in English.',
  pgCtaTitle: 'Know your date and guest count?',
  pgCtaBody: 'Search by city, date and capacity, see which sessions are free, and send a request to the venue. Nothing is paid online.',

  // event types (/occasions)
  pgOccEyebrow: 'Event types',
  pgOccTitle: 'Spaces for every kind of event',
  pgOccLead:
    'Start with what you are planning. Each occasion comes with practical advice and the venues whose layout and facilities usually suit it.',
  pgOccGroupsEyebrow: 'Occasions',
  pgOccGroupsTitle: 'Choose your occasion',
  pgOccGroupsSub: 'Planning advice, suitable venues and guides for each.',
  pgOccOpen: 'Advice and venues',
  pgAllTypesEyebrow: 'Browse by event',
  pgAllTypesTitle: 'All event types',
  pgAllTypesSub: 'Go straight to venues that usually suit a particular event.',

  // one occasion (/occasions/:slug)
  pgOccNotFoundTitle: 'We could not find that occasion',
  pgOccNotFoundBody: 'The link may be out of date. Every event type is listed on one page.',
  pgOccVenuesTitle: 'Venues for {name}',
  pgOccVenuesEmptyTitle: 'No matching venues listed yet',
  pgOccVenuesEmptyBody: 'New venues are joining. Meanwhile, browse every venue and filter by city and guest count.',
  pgOccEventsTitle: 'Search by event',
  pgOccGuidesEyebrow: 'Read before you book',
  pgOccGuidesTitle: 'Guides for planning',
  pgOccSeeAll: 'See all venues for this occasion',

  // weddings
  pgTipWed1: 'Guest count, honestly',
  pgTipWed1Body:
    'Compare your realistic guest list with the hall’s seating and its floating capacity. The crowd peaks around the ceremony; the meal is usually served in batches.',
  pgTipWed2: 'Dining space and catering rules',
  pgTipWed2Body:
    'Ask how many the dining hall seats at once, and whether you must use the venue’s caterer or may bring your own for the sadya.',
  pgTipWed3: 'Parking for cars and two-wheelers',
  pgTipWed3Body: 'Wedding guests arrive together. Check parking for both, and a drop-off point at the door for elders.',
  pgTipWed4: 'A stage that photographs well',
  pgTipWed4Body:
    'Look at the stage size, backdrop and lighting, and the floor space in front of it for photographers and video.',
  pgTipWed5: 'Rooms for the families',
  pgTipWed5Body: 'A bridal room and a changing room near the stage save a lot of running around on the day.',
  pgTipWed6: 'Power backup that covers everything',
  pgTipWed6Body: 'Ask whether the generator runs the air conditioning, lights and sound, not just the fans.',

  // conferences
  pgTipConf1: 'Screen, sound and projector',
  pgTipConf1Body:
    'Confirm the projector or LED screen, how many wireless microphones there are, and how laptops connect at the podium.',
  pgTipConf2: 'Seating layout',
  pgTipConf2Body:
    'Theatre style fits the most people; classroom style with tables fits fewer but suits note-taking. Ask what the hall can set up.',
  pgTipConf3: 'Registration and breakout space',
  pgTipConf3Body: 'Look for a foyer for the registration desk, and separate rooms if your agenda has parallel sessions.',
  pgTipConf4: 'Air conditioning and power backup',
  pgTipConf4Body: 'A full day in a closed hall needs reliable cooling and a generator that keeps the AV running.',
  pgTipConf5: 'Internet for presenters',
  pgTipConf5Body: 'Ask whether Wi-Fi is available and how many people it can take, and keep a mobile hotspot as backup.',
  pgTipConf6: 'Access and arrival',
  pgTipConf6Body: 'Parking, a lift for upper floors, and how easily delegates from out of town can reach the venue.',

  // seminars and workshops
  pgTipSem1: 'A room that fits',
  pgTipSem1Body: 'A half-empty hall feels flat. Pick a room close to your registrations, with a small margin.',
  pgTipSem2: 'Seating for learning',
  pgTipSem2Body: 'Classroom or U-shape seating for workshops where people write or work together; theatre style for talks.',
  pgTipSem3: 'Screen and writing space',
  pgTipSem3Body: 'Check the screen is readable from the back row, and ask about whiteboards or flip charts.',
  pgTipSem4: 'The right session',
  pgTipSem4Body: 'Book the morning, afternoon or full-day session that matches your agenda, with time to set up first.',
  pgTipSem5: 'Tea and lunch',
  pgTipSem5Body: 'Ask where refreshments can be served without disturbing the room, and the venue’s catering rules.',

  // cultural events
  pgTipCul1: 'Stage size and wings',
  pgTipCul1Body: 'Measure the stage against your largest item, and check for wings or a backstage area for entries.',
  pgTipCul2: 'Sound and lighting',
  pgTipCul2Body: 'Ask what sound system and stage lights are included, and whether your own technicians can use them.',
  pgTipCul3: 'Sightlines',
  pgTipCul3Body: 'Tiered seating gives everyone a view. On a flat floor, a raised stage matters even more.',
  pgTipCul4: 'Green rooms',
  pgTipCul4Body: 'Performers need changing rooms near the stage, with mirrors, lights and power points.',
  pgTipCul5: 'Time to rehearse',
  pgTipCul5Body: 'Book enough time before the audience arrives for setup and a full sound check.',

  // parties and celebrations
  pgTipCel1: 'Size it to the guest list',
  pgTipCel1Body: 'A hall that feels full beats one that feels empty. Round tables need more space per guest than rows.',
  pgTipCel2: 'Food and cake',
  pgTipCel2Body: 'Check whether outside catering or your own cake is allowed, and where it can be served.',
  pgTipCel3: 'Decoration rules',
  pgTipCel3Body: 'Ask what you can put up — balloons, lights, a backdrop — and when the decorators can come in.',
  pgTipCel4: 'Music and finishing time',
  pgTipCel4Body: 'Confirm any sound limits and when the event must end, especially for evening parties.',
  pgTipCel5: 'Children and elders',
  pgTipCel5Body: 'A safe corner for children, seats near the entrance for elders, and a lift or ramp if needed.',

  // corporate events
  pgTipCorp1: 'Room for your brand',
  pgTipCorp1Body: 'Check space for a branded backdrop, standees and a stage for speakers or product demonstrations.',
  pgTipCorp2: 'AV that works',
  pgTipCorp2Body: 'Projector or LED screen, wireless microphones, and a technician present for the whole event.',
  pgTipCorp3: 'Arrival and check-in',
  pgTipCorp3Body: 'A reception area for check-in, comfortable seating, and a separate space for meals.',
  pgTipCorp4: 'Reliability first',
  pgTipCorp4Body: 'Air conditioning, power backup and a confirmed timetable matter more than decoration.',
  pgTipCorp5: 'Receipts for your accounts',
  pgTipCorp5Body: 'Ask the venue for a receipt when you pay them.',

  // locations
  pgLocEyebrow: 'Locations',
  pgLocTitle: 'Venues by city',
  pgLocLead: 'Every city here has venues you can request today. Pick one to see its venues, prices and free dates.',
  pgLocUpTo: 'Up to {count} guests',
  pgLocView: 'See venues in {city}',
  pgLocPhotoAlt: 'A venue in {city}',
  pgLocEmptyTitle: 'No cities listed yet',
  pgLocEmptyBody: 'Venues are being verified and will appear here soon. You can still browse what is listed.',

  // inspiration
  pgInspEyebrow: 'Inspiration',
  pgInspTitle: 'Guides for planning your event',
  pgInspLead: 'Practical advice on choosing a hall, laying it out and getting the details right, written for events in Kerala.',
  pgInspFilter: 'Filter guides by topic',
  pgCatAll: 'All',
  pgCatPlanning: 'Planning',
  pgCatWeddings: 'Weddings',
  pgCatCorporate: 'Corporate',
  pgCatVenues: 'Venues',
  pgFeatured: 'Featured guide',
  pgInspEmpty: 'No guides on this topic yet.',
  pgInspCtaTitle: 'Ready to look at venues?',
  pgInspCtaBody: 'Search by city, date and guest count, and compare two venues side by side.',

  // article
  pgArtNotFoundTitle: 'We could not find that guide',
  pgArtNotFoundBody: 'It may have moved. All our planning guides are on one page.',
  pgAllGuides: 'All guides',
  pgTip: 'Tip',
  pgPlanTitle: 'Plan with Aghosha',
  pgPlanBody:
    'Search venues by city and guest count, check free sessions for your date, and send a request. The venue confirms it, and you pay the venue directly.',
  pgPlanOccasions: 'Browse by event type',
  pgMoreGuides: 'More guides',

  // about
  pgAboutEyebrow: 'About Aghosha',
  pgAboutTitle: 'A simpler way to find and book a venue for your event',
  pgAboutLead:
    'Auditoriums, wedding halls, banquet halls and convention centres in one place, with real capacity, prices and free dates. Planning starts with facts, not phone calls.',
  pgAboutWhoEyebrow: 'Who it is for',
  pgAboutWhoTitle: 'Built for both sides of a booking',
  pgAboutGuestsTitle: 'People planning an event',
  pgAboutGuestsBody:
    'Families planning a wedding, companies running a conference, associations hosting a programme. Compare venues by capacity, facilities, price and free dates before you make a single call.',
  pgAboutOwnersTitle: 'Venue owners and managers',
  pgAboutOwnersBody:
    'List your venues once, receive booking requests, keep one calendar for every venue, and manage the day from the web or the Aghosha Manager app.',
  pgAboutWhyEyebrow: 'Why we built it',
  pgAboutWhyTitle: 'Booking a venue should not take a week of phone calls',
  pgAboutWhy1:
    'Finding a venue usually means asking around, calling venues one by one and visiting before you even know whether your date is free or the venue is big enough.',
  pgAboutWhy2:
    'Aghosha puts the facts first: how many guests a hall seats, what a session costs, which facilities it has and which dates are still open. You shortlist from home and talk to the venue when you are ready.',
  pgAboutWhyAlt: 'Planning notes and a laptop on a table',
  pgHowEyebrow: 'How it works',
  pgHowTitle: 'From first search to confirmed booking',
  pgHowSub: 'A booking on Aghosha is a request, not a payment. The venue confirms it, and you pay the venue directly.',
  pgHowGuests: 'For guests',
  pgHowOwners: 'For venue owners',
  pgHowG1: 'Discover',
  pgHowG1Body: 'Search by city, date, event type and guest count. Every listing shows capacity, facilities, photos and prices.',
  pgHowG2: 'Compare',
  pgHowG2Body: 'Put two venues side by side, check free sessions on your date and read reviews from guests who booked.',
  pgHowG3: 'Request',
  pgHowG3Body: 'Choose a session and send a booking request with your event details. Nothing is paid online.',
  pgHowG4: 'The venue confirms, you pay them',
  pgHowG4Body:
    'The venue confirms your request and you are told by SMS or WhatsApp. You pay the venue directly, at the venue.',
  pgHowO1: 'Sign up',
  pgHowO1Body: 'Create a venue account with your mobile number.',
  pgHowO2: 'Get verified',
  pgHowO2Body:
    'Upload a government ID and your venue licences, such as the occupancy certificate. An Aghosha admin checks them before you go live.',
  pgHowO3: 'List your venues',
  pgHowO3Body: 'Add photos, capacity, facilities, session times and prices for each venue.',
  pgHowO4: 'Manage bookings',
  pgHowO4Body: 'Confirm requests, record payments and keep the calendar up to date on the web or in the Aghosha Manager app.',
  pgHowO5: 'Add supervisors',
  pgHowO5Body: 'Give your staff their own logins to see the booking calendar.',
  pgTrustEyebrow: 'Why you can trust it',
  pgTrustTitle: 'Real venues, real availability',
  pgTrust1: 'Checked by an admin',
  pgTrust1Body: 'Every venue manager’s ID and venue licences are checked by an Aghosha admin before a listing goes live.',
  pgTrust2: 'Availability you can see',
  pgTrust2Body: 'Free and booked sessions come from the calendar the venue manages on Aghosha, so you request dates that are open.',
  pgTrust3: 'Prices up front',
  pgTrust3Body: 'Session prices are on the listing before you ask, and you pay the venue, not us.',
  pgTrust4: 'Your details stay private',
  pgTrust4Body: 'Only the venue you book sees your booking details. We do not sell data or run ad trackers.',
  pgTrustPrivacy: 'Read the privacy policy',
  pgStatsEyebrow: 'Aghosha today',
  pgStatsTitle: 'Live from the platform',
  pgStatVenues: { one: 'Venue listed', other: 'Venues listed' },
  pgStatCities: { one: 'City', other: 'Cities' },
  pgStatCapacity: 'Guests in our largest venue',
  pgStatEvents: { one: 'Event confirmed', other: 'Events confirmed' },
  pgStatRating: 'Average guest rating',
  pgFaqEyebrow: 'Questions',
  pgFaqTitle: 'Questions and answers',
  pgFaq1: 'Is there a booking fee?',
  pgFaq1A:
    'No. Searching and sending a booking request on Aghosha is free for guests. You pay the venue’s price, as shown on the listing, to the venue itself.',
  pgFaq2: 'How do I pay?',
  pgFaq2A:
    'Directly at the venue. There is no advance to pay when you book — once the venue confirms, you pay them the amount shown, at the venue. Aghosha takes no payments online, so your card and bank details never reach us.',
  pgFaq3: 'Does the venue confirm my booking?',
  pgFaq3A:
    'Yes. Your request goes to the venue’s manager, who confirms it. Until then the slot is not held for you. You see the status in your bookings and get updates by SMS or WhatsApp.',
  pgFaq4: 'Can I cancel?',
  pgFaq4A:
    'Yes, from your bookings. A request the venue has not confirmed can be withdrawn at no cost. If you have paid the venue, what you get back depends on how close the event is: all of it 48 hours or more before, half between 12 and 48 hours, and nothing in the last 12 hours. The venue returns the money to you directly.',
  pgFaq5: 'How do I know a venue is real?',
  pgFaq5A:
    'Before a venue goes live, an Aghosha admin checks the manager’s government ID and the venue’s licences, such as the occupancy certificate. Reviews come only from guests who booked.',
  pgFaq6: 'Can I visit before booking?',
  pgFaq6A:
    'Yes. Each listing has the venue’s address and contact number, so you can call and arrange a visit. Many listings also have several photos, and some have a 360° view.',
  pgFaq7: 'Is the site available in Malayalam?',
  pgFaq7A:
    'Yes. Switch between English and Malayalam from the language menu at any time. Some planning guides and venue descriptions are in English only.',
  pgFaq8: 'How do I list my venue?',
  pgFaq8A:
    'Choose “List your venue”, sign up with your mobile number and upload your ID and venue licences. Once an admin approves your account, add your venues and start receiving requests.',
  pgFaq9: 'What is the Aghosha Manager app?',
  pgFaq9A:
    'The mobile app for venue owners and their staff. Managers see and answer booking requests, check the calendar and get notified of new bookings. They can also add supervisors, who see the calendar only.',
  pgFaq10: 'How are my details used?',
  pgFaq10A:
    'Only to run your booking. The venue you book sees your name, number and what you booked. We do not sell your data and there are no ad trackers on the site. The privacy policy has the details.',
  pgFaqMoreTitle: 'Still have a question?',
  pgFaqMoreBody: 'Write to us or call. We are happy to help you find the right venue.',
  pgAboutOperator: 'Aghosha is built and run by {operator}, Kochi, Kerala.',
  pgAboutCtaTitle: 'Start with a search',
  // night edition: shared
  pgEventType: 'Event type',
  pgOccPicker: 'Choose an event type',
  pgOccHeroTitle: 'Venues for {name}',
  pgOccTipsEyebrow: 'Planning tips',
  pgOccPlanning: 'Planning {name}',
  pgOccSearchThese: 'Search these venues',
  pgOccSuitableWord: { one: 'suitable venue', other: 'suitable venues' },
  pgOccSummary: 'Venues whose layout and facilities usually suit {name}. Check dates and details with each venue.',
  pgFactVenues: 'Suitable venues',
  pgFactUpTo: 'Up to',
  pgFactGuests: 'guests',
  pgFactFrom: 'From',
  pgFactCities: 'Cities',
  pgLocCityWord: { one: 'city with listed venues', other: 'cities with listed venues' },
  pgHowFor: 'Show steps for',
  pgAboutFinalTitle: 'See the venues in your city',
  pgAboutFinalBody: 'Real capacity, prices and free dates.',
  pgOnThisPage: 'On this page',
  pgCtSentTitle: 'Message sent',
  pgCtSentBody: 'Thanks. We will get back to you soon.',
  pgCtSendAnother: 'Send another message',
  pgCtSubBooking: 'Booking a venue',
  pgCtSubListing: 'Listing my venue',
  pgCtSubAccount: 'Account or login',
  pgCtSubOther: 'Something else',
  pgCtMessageError: 'Tell us a little about what you need.',
} satisfies Record<string, Phrase>;
